import { BehaviorSubject } from 'rxjs';
import { handleResponse } from '../helpers/handleResponse';
import { prefetchTeamsCache } from '../hooks/useTeamsCache';
import { prefetchSubscriptionStatus } from '../hooks/useSubscriptionStatus';


// Guarded for SSR: this module is imported (transitively, via apiService -> authHeader)
// by every page component, including the ones rendered server-side in src/entry-server.js.
// localStorage doesn't exist in Node, so an unguarded read here would crash on import.
const storedUser = typeof localStorage !== 'undefined' ? localStorage.getItem('currentUser') : null;
const currentUserSubject = new BehaviorSubject(storedUser ? JSON.parse(storedUser) : null);

export const authenticationService = {
    register,
    login,
    forget,
    reset,
    logout,
    update,
    performLogin,
    updateRolesPermissions,
    updateStripeCustomerId,   
    currentUser: currentUserSubject.asObservable(),
    get currentUserValue() { return currentUserSubject.value },
    currentUserSubject
};

function login(email, password) {

    let data = new FormData();
    data.append('email', email);
    data.append('password', password);

    const requestOptions = {
        method: 'POST',
        //headers: { 'Content-Type': 'application/json' },
        body: data
    };

    return fetch(import.meta.env.VITE_API_URL + '/login', requestOptions)
        .then(res => {
            if (res.status !== 200 && res.status !== 201) {              
                const error = new Error('Request failed');
                error.status = res.status;
                error.url = res.url;
                throw error;
            } else {
                return res;
            }
        })
        .then(handleResponse)
        .then(user => {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem('currentUser', JSON.stringify(user));
            currentUserSubject.next(user);

            // Eagerly populate caches now that we have an auth token
            prefetchTeamsCache();
            prefetchSubscriptionStatus();

            return user;
        });
}
function forget(email) {

    let data = new FormData();
    data.append('email', email);

    const requestOptions = {
        method: 'POST',
        //headers: { 'Content-Type': 'application/json' },
        body: data
    };

    return fetch(import.meta.env.VITE_API_URL + '/reset-password', requestOptions)
        .then(res => {
            if (res.status !== 200 && res.status !== 201) {
                const error = new Error('Request failed');
                error.status = res.status;
                error.url = res.url;
                throw error; 
            } else {
                return res;
            }
        }
        )
        .then(handleResponse)
        .then(user => {
            return user;
        });
}

function reset(password, email, token) {

    let data = new FormData();
    data.append('password', password);
    data.append('token', token);
    data.append('email', email);

    const requestOptions = {
        method: 'POST',
        //headers: { 'Content-Type': 'application/json' },
        body: data
    };

    return fetch(import.meta.env.VITE_API_URL + '/reset/password', requestOptions)
        .then(res => {
            if (res.status !== 200 && res.status !== 201) {
                const error = new Error('Request failed');
                error.status = res.status;
                error.url = res.url;
                throw error;
            } else {
                return res;
            }
        }
        )
        .then(handleResponse)
        .then(user => {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem('currentUser', JSON.stringify(user));
            currentUserSubject.next(user);

            // Eagerly populate caches now that we have an auth token
            prefetchTeamsCache();
            prefetchSubscriptionStatus();

            return user;
        });
}

function register(name, email, phone, password, tsCs, reCaptchaToken) {
    
    let data = new FormData();
    data.append('name', name);
    data.append('email', email);
    data.append('phone', phone);
    data.append('password', password);
    data.append('tc', tsCs ? 'true' : 'false');
    data.append('token',reCaptchaToken);

    const requestOptions = {
        method: 'POST',
        body: data
    };

    return fetch(import.meta.env.VITE_API_URL + '/subscribe', requestOptions)
        .then(res => {
            if (res.status !== 200 && res.status !== 201) {
                const error = new Error('Request failed');
                error.status = res.status;
                error.url = res.url;
                throw error;
            }
            return res;
        })
        .then(handleResponse)
        .then(user => {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem('currentUser', JSON.stringify(user));
            currentUserSubject.next(user);

            // Eagerly populate caches now that we have an auth token
            prefetchTeamsCache();
            prefetchSubscriptionStatus();

            return user;
        });
}

function performLogin(user) {
   
    localStorage.setItem('currentUser', JSON.stringify(user));
    currentUserSubject.next(user);
}

function update(name, email, phone) {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    user.name = name;
    user.email = email;
    user.phone = phone;
    localStorage.setItem('currentUser', JSON.stringify(user));
    currentUserSubject.next(user);
}
function updateRolesPermissions(roles,permissions) {
    
    const user = JSON.parse(localStorage.getItem('currentUser'));
    user.roles = roles;
    user.permissions = permissions;
    localStorage.setItem('currentUser', JSON.stringify(user));
    currentUserSubject.next(user);
}
function updateStripeCustomerId(stripeCustomerId) {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    user.stripeCustomerId = stripeCustomerId;  
    localStorage.setItem('currentUser', JSON.stringify(user));
    currentUserSubject.next(user);
}
function logout() {
    // remove user data from local and session storage to log user out
    localStorage.removeItem('currentUser');
    localStorage.removeItem('sk_prediction_views');
    localStorage.removeItem('teams_data');
    sessionStorage.removeItem('subscription_data');
    sessionStorage.removeItem('subscription_bar_dismissed');
    currentUserSubject.next(null);
}
